/**
 * 69.52 step 3 (ARCHITECTURE §5.27 (4); DECISIONS 261 (4), 262 (b), (c)): a ship's evasion under fire. A flight on its way in
 * (not landed, not flown by a person) is under fire when an enemy's seeker has locked it (its warning), a missile is after it, or its
 * hull was hit since the tick began (point defence's flak, a rocket, a missile's burst). Its pilot answers by its risk:
 * - **breaking off** (below SKY_EVADE.abortBelow, the cautious default — 262 (c): "may abort a delivery"): the approach given
 *   up, a fresh route home from where it is (69.17's rejoin bridge, core/dropship.ts rejoinFlight), its riders not delivered
 *   (the flight gone, they come back with the waves; a wave ship's aboard alive come out down its ramp on its pad, core/
 *   dropship.ts pickupHome); never on a hot run;
 * - **dropping low** (a middle risk): down to SKY_EVADE.napPeople over the ground for `lowSeconds` — under the radar's floor
 *   and behind the relief, where a lock being held breaks with the line of sight; it presses on;
 * - **jinking** (from SKY_EVADE.boldFrom, and a hot run): an S across its track for `jinkSeconds` — point defence's crossing
 *   angle up; it presses on.
 * Recorded on the flight (`evade`: its kind and tick) and drawn into its pose as closed-form terms (core/skyRoute.ts evadeTerms), so
 * the pose stays a pure function of the tick; a jink or a drop low may come again once the last is done.
 * 63.13 (DECISIONS 277): a gunship's pilot answers by its own rule (gunshipAnswer): it presses on to its station. Pure.
 */
import type { DropshipFlight, GameState } from '../types';
import { SKY_EVADE } from '../constants/action';
import { rejoinFlight } from './dropship';
import { flightNow } from './shipNow';
import { pilotRisk, evadeSeconds } from './skyRoute';
import { PHYS_DT } from '../constants';
import { GUNSHIP } from '../constants/gunship';
import { shipBodyOf } from './shipBody';

export type EvadeKind = 'jink' | 'low' | 'break';

/** The manoeuvre a pilot at `risk` makes under fire (a hot run never breaks off). */
export function evadeKind(risk: number, hot: boolean): EvadeKind {
  if (hot || risk >= SKY_EVADE.boldFrom) return 'jink';
  return risk >= SKY_EVADE.abortBelow ? 'low' : 'break';
}

/** Is flight `f` under fire at this tick: locked by an enemy's seeker, a missile after it, or its hull hit since `before`? */
export function underFire(state: Pick<GameState, 'players' | 'missiles'>, f: DropshipFlight, before: DropshipFlight | undefined): boolean {
  if (before && (f.health ?? Infinity) < (before.health ?? Infinity)) return true;
  if ((state.missiles?.live ?? []).some(m => m.team !== f.team && m.target === f.id)) return true;
  return state.players.some(p => p.teamIndex !== f.team && p.alive && p.missileLock?.state === 'lock' && p.missileLock.target === f.id);   // (a lock: the ship's warning — a seeker still tracking is no fire yet)
}

/**
 * The flights' answers this tick: each on its way in, under fire and not in a manoeuvre, starts its pilot's (`before`: the
 * flights at the tick's start, for the hits); a gunship by its own rule (gunshipAnswer). The same state when none does.
 */
export function stepEvasion(state: GameState, before: readonly DropshipFlight[] | undefined, tick: number): GameState {
  const a = state.dropships;
  if (!a?.flights.length) return state;
  let flights: DropshipFlight[] | null = null;
  a.flights.forEach((f, i) => {
    if (f.piloted || tick <= f.calledTick) return;
    if (f.gunship || f.payload === 'gunship') { const g = gunshipAnswer(state, f, before?.find(b => b.id === f.id), tick); if (g) (flights ??= [...a.flights])[i] = g; return; }   // (69.74 step 1: a helicopter on station too)
    if (tick >= f.landTick) return;
    if (f.evade && (f.evade.kind === 'break' || (tick - f.evade.tick) * PHYS_DT < evadeSeconds(f.evade.kind))) return;
    if (!underFire(state, f, before?.find(b => b.id === f.id))) return;
    const kind = evadeKind(f.risk ?? pilotRisk(state.config, f.team), !!f.hot);
    let next: DropshipFlight = { ...f, evade: { kind, tick } };
    if (kind === 'break') {
      // the approach given up: a fresh route home from where it is, bridged from its place and velocity (69.17's rejoin)
      const now = flightNow(state, f, tick);   // (its place and velocity: the ships' one reader, core/shipNow.ts)
      if (!now?.flight) return;
      next = { ...rejoinFlight(state, f, tick, { pos: now.flight.pose.pos, vel: now.vel }, f.origin, true), evade: { kind, tick } };
    }
    (flights ??= [...a.flights])[i] = next;
  });
  return flights ? { ...state, dropships: { ...a, flights } } : state;
}

/**
 * 63.13 (DECISIONS 277; the owner's gunship broke off on its way in at a lock and left as it arrived): a gunship's answer under
 * fire. Its pilot's risk is the side's dial (69.52), else GUNSHIP.risk (pressing on; a hot call's 1). It presses on, jinking on its
 * way in (none started that would run past where it joins its orbit, so its pose joins whole); a cautious one (the dial under
 * SKY_EVADE.abortBelow) breaks off for home, on its way in or on station, once its hull is at or under GUNSHIP.breakHull of whole —
 * real damage, never a lock alone. Null: no answer. 69.74 step 1: a helicopter's fire support by the same rule, of its own hull.
 */
function gunshipAnswer(state: GameState, f: DropshipFlight, before: DropshipFlight | undefined, tick: number): DropshipFlight | null {
  if (tick >= f.liftTick || f.evade?.kind === 'break') return null;
  const inbound = tick < f.landTick;
  if (inbound && f.evade && (tick - f.evade.tick) * PHYS_DT < evadeSeconds(f.evade.kind)) return null;
  if (!underFire(state, f, before)) return null;
  const risk = f.hot ? 1 : state.config.pilotRisk?.[f.team] ?? GUNSHIP.risk;
  const hull = shipBodyOf(f).health;
  if (risk < SKY_EVADE.abortBelow && (f.health ?? hull) <= GUNSHIP.breakHull * hull) {
    const now = flightNow(state, f, tick);
    if (!now?.flight) return null;
    return { ...rejoinFlight(state, f, tick, { pos: now.flight.pose.pos, vel: now.vel }, f.origin, true), evade: { kind: 'break', tick } };
  }
  if (!inbound || (f.landTick - tick) * PHYS_DT < evadeSeconds('jink')) return null;
  return { ...f, evade: { kind: 'jink', tick } };
}
